import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"
import { red } from "@mui/material/colors";

import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { isEmpty } from "lodash";

import transformHistoryService from "@plannel/services/data/transform-history-service";
import PaginationContainer from "@plannel/components/PaginationContainer"
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import Snackbar from "@plannel/components/Snackbar";
import SegmentedControl from "@plannel/components/ui/SegmentedControl";
import { StatusChip, formatDuration, secondsBetween } from "./HistoryGridParts";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "../utils/color-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import CodeUtil from "../utils/code-util";
import { Autocomplete, TextField } from "@mui/material";

const { snackBarMessageColor } = ColorUtil;
const { scheduleStatusObj: { STATUS_SUCCESS, STATUS_FAILURE, STATUS_WARNING } } = CodeUtil;

/*
 * 변환 이력은 작업이 끝난 뒤에 한꺼번에 저장된다(DataTransformService) → "실행 중" 상태가 없다.
 * 세그먼트 값이 곧 status_cd 다. "all" 만 조건 없음.
 */
const STATUS_FILTERS = ["all", STATUS_SUCCESS, STATUS_WARNING, STATUS_FAILURE];
const STATUS_TONE = { [STATUS_SUCCESS]: "success", [STATUS_WARNING]: "warning", [STATUS_FAILURE]: "error" };

// 정렬은 서버로 colId 를 그대로 넘긴다(orderByColumn) → 화면 전용 colId 는 실제 필드로 바꿔 보낸다.
const SORT_FIELD_BY_COL_ID = { transformStatus: "statusCd", transformPeriod: "periodStart" };

const TransformStatusChip = ({ value }) => (value ? <StatusChip label={value} tone={STATUS_TONE[value]} /> : null);

// 서버가 주는 tfDuration("1m 3s")은 정렬·정렬선이 맞지 않아 Import History 와 같은 m:ss 로 다시 계산한다.
const durationGetter = ({ data }) => secondsBetween(data?.tfStartTs, data?.tfEndTs);

const TransformHistory = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const { dpTransformMeasure: { ACTUAL_SALES, ACTUAL_SALES_YOY, FINANCE_PLAN, SALES_ORDER, SALES_PLAN, AVG_MEASURE, POS_ACTUAL_SALES, POS_ACTUAL_SALES_YOY, POS_AVG_MEASURE }, transformSourceData: { INVENTORY_AGG },
    SHOW_POS } = CodeUtil;
  // v26.10: POS 미출시 — 필터 선택지에서 POS 변환 셋을 뺀다(code-util SHOW_POS)
  const transformMeasures = [ACTUAL_SALES, ACTUAL_SALES_YOY, FINANCE_PLAN, INVENTORY_AGG, SALES_ORDER, SALES_PLAN, AVG_MEASURE,
    ...(SHOW_POS ? [POS_ACTUAL_SALES, POS_ACTUAL_SALES_YOY, POS_AVG_MEASURE] : [])];

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.page ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [tPages, setTotalPages] = useState(0);

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [targetName, setTargetName] = useState(reduxViewState?.searchCodes?.jobSearchCode?.targetName ?? null);
  // 예전 화면은 상태를 드롭다운(jobSearchCode.statusCd)으로 저장했다 — 남아 있으면 그대로 이어받는다.
  const [statusFilter, setStatusFilter] = useState(() => {
    const saved = reduxViewState?.searchCodes?.statusFilter ?? reduxViewState?.searchCodes?.jobSearchCode?.statusCd;
    return STATUS_FILTERS.includes(saved) ? saved : "all";
  });
  const [sortParams, setSortParams] = useState({});
  const [dataHistories, setDataHistories] = useState([]);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const translationValueFormatter = params => (params?.value ? t(params.value) : "");

  const periodFormatter = ({ data }) => {
    if (!data?.periodStart || !data?.periodUom) {
      return "";
    }
    const start = dateUtils.headerDateFormatter(data.periodStart, data.periodUom);
    const end = data.periodEnd ? dateUtils.headerDateFormatter(data.periodEnd, data.periodUom) : "";
    const range = end && end !== start ? `${start} ~ ${end}` : start;
    return `${range} · ${t(data.periodUom)}`;
  }

  const columnDefs = [
    {
      headerName: "status", colId: "transformStatus", field: "statusCd", type: "nonEditableColumn", width: 120,
      cellRenderer: TransformStatusChip,
    },
    { headerName: "targetName", field: "targetName", type: "nonEditableColumn", valueFormatter: translationValueFormatter, width: 200 },
    { headerName: "period", colId: "transformPeriod", field: "periodStart", type: "nonEditableColumn", valueFormatter: periodFormatter, width: 230 },
    { headerName: "option", field: "option", type: "nonEditableColumn", valueFormatter: translationValueFormatter, width: 150 },
    { headerName: "startTs", field: "tfStartTs", type: "nonEditableColumn", valueFormatter: dateUtils.formatDateTime, width: 150 },
    {
      headerName: "duration", colId: "duration", type: ["nonEditableColumn", "rightAligned"], width: 110, sortable: false,
      valueGetter: durationGetter, valueFormatter: formatDuration,
    },
    {
      headerName: "message", field: "errorMsg", type: "nonEditableColumn", flex: 1, minWidth: 200, tooltipField: "errorMsg",
      cellStyle: params => (params?.data?.statusCd === STATUS_FAILURE ? { color: red[700] } : null),
    },
  ];

  const getRequestParams = () => {
    const params = {};

    if (targetName) {
      params.targetName = targetName;
    }

    if (statusFilter !== "all") {
      params.statusCd = statusFilter;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    return { ...params, ...sortParams };
  };

  const retrieveTransformHistories = () => {
    const params = getRequestParams();
    transformHistoryService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        setDataHistories(results);
        setTotalPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const searchCodes = { statusFilter, jobSearchCode: { targetName } };
        const reduxState = reduxUtil.createViewState(viewName, searchCodes, currentPage, pageSize);
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      })
  }

  const onGridReady = (params) => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true, alignOrderToDefs: true });
  };

  const onSortChanged = params => {
    const sortState = GridUtils.getSortState(params);
    const field = SORT_FIELD_BY_COL_ID[sortState.orderByColumn];
    setSortParams(field ? { ...sortState, orderByColumn: field } : sortState);
  };

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveTransformHistories();
    }
  }

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveTransformHistories();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetName, statusFilter]);

  return (
    <>
      <FilterContainer hasIcons={false}>
        <Autocomplete
          options={transformMeasures}
          getOptionLabel={option => t(option)}
          value={targetName}
          onChange={(_, newValue) => setTargetName(newValue)}
          isOptionEqualToValue={(option, value) => option === value }
          size="small"
          sx={{ mr: 1.5, minWidth: 220 }}
          renderInput={(params) => (
            <TextField {...params} label={t("targetName")} />
          )}
        />
        <SegmentedControl
          value={statusFilter}
          onChange={setStatusFilter}
          options={STATUS_FILTERS.map(key => ({ value: key, label: t(key) }))}
        />
      </FilterContainer>
      <Box id="data-history-grid" className="ag-theme-balham grid-area" mb={1} >
        <AgGridReact
          ref={gridRef}
          rowData={dataHistories}
          {...defaultGridMemo}
          onGridReady={onGridReady}
          onSortChanged={onSortChanged}
        />
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(TransformHistory);
